import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Factory, Plus } from 'lucide-react'
import { listProductionProducts, listRuns, productionNeeds, startRun } from '../../api/production'
import Button from '../../components/Button'
import ErrorBanner from '../../components/ErrorBanner'
import HelpTip from '../../components/HelpTip'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { fmtDay, localToday, money } from '../../utils/format'
import { Field } from '../master/masterShared'
import InventoryTabs from './InventoryTabs'
import { qty, useLocations } from './inventoryShared'
import { RUN_STATUS } from './productionShared'
import { ProductionGuide } from './ProductionGuide'
import { CreateOption } from '../../components/CreateOption'

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'Open', label: 'In progress' },
  { id: 'Finished', label: 'Finished' },
  { id: 'Cancelled', label: 'Cancelled' },
]

// Production runs: make a batch of a product. Materials (from stock or bought
// for the batch), labour and overhead collect on the run; finishing it puts
// the good units into stock at total cost ÷ units made.
export default function Production() {
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const [rows, setRows] = useState(null)
  const [error, setError] = useState('')
  const [starting, setStarting] = useState(false)
  const filter = FILTERS.some((f) => f.id === params.get('show')) ? params.get('show') : 'all'
  const setFilter = (id) => setParams(id === 'all' ? {} : { show: id }, { replace: true })

  useEffect(() => {
    listRuns().then((r) => setRows(Array.isArray(r) ? r : [])).catch((e) => { setRows([]); setError(e.message || 'Could not load the runs.') })
  }, [])
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.id, (rows || []).filter((r) => f.id === 'all' || r.status === f.id).length])), [rows])
  const shown = (rows || []).filter((r) => filter === 'all' || r.status === filter)

  return (
    <div className="ci-page">
      <InventoryTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Production <HelpTip text="Make a batch of a product, such as 50 T-shirts. Add the materials (from stock, or bought for the batch), labour and overhead to the run; finishing it divides the total by the units made and puts them into stock at that cost." /></h1>
            <div className="ci-title-meta">Make products from your materials, and know what each one cost.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <Link className="ci-btn ci-btn-ghost" to="/inventory/production/products">What you make</Link>
          <Link className="ci-btn ci-btn-ghost" to="/inventory/production/cogm">Cost report</Link>
          <button type="button" className="ci-btn ci-btn-primary" onClick={() => setStarting(true)}><Plus size={15} />Start a run</button>
        </div>
      </div>

      <ProductionGuide />
      {error && <ErrorBanner message={error} />}
      <FilterChips options={FILTERS.map((f) => ({ ...f, count: counts[f.id] }))} value={filter} onChange={setFilter} />

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead>
              <tr>
                <th>Run</th>
                <th>Making</th>
                <th className="num">Units</th>
                <th className="num hide-sm">Cost</th>
                <th className="num">Per unit</th>
                <th className="center">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows == null ? (
                <tr><td colSpan="6"><Spinner label="Loading runs…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="6"><div className="ci-empty">{rows.length ? 'No runs match.' : 'No runs yet. Start one for a batch of something you make.'}</div></td></tr>
              ) : shown.map((r) => {
                const st = RUN_STATUS[r.status] || RUN_STATUS.Open
                return (
                  <tr key={r.run_id} className="row-link" onClick={() => nav(`/inventory/production/${r.run_id}`)}>
                    <td>
                      <Link className="ci-je-link" to={`/inventory/production/${r.run_id}`} onClick={(e) => e.stopPropagation()}>{r.label}</Link>
                      <span className="cell-sub">{fmtDay(r.started_on)}{r.finished_on ? ` – ${fmtDay(r.finished_on)}` : ''}</span>
                    </td>
                    <td>{r.product_name}</td>
                    <td className="num">{r.status === 'Finished' ? `${qty(r.made_qty)} made` : qty(r.planned_qty)}{r.scrap_qty > 0 && <span className="cell-sub">{qty(r.scrap_qty)} wasted</span>}</td>
                    <td className="num hide-sm">{money(r.total_cost)}</td>
                    <td className="num">{r.unit_cost != null ? money(r.unit_cost) : '–'}</td>
                    <td className="center"><StatusBadge label={st.label} tone={st.tone} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {starting && <StartRun onClose={() => setStarting(false)} onStarted={(r) => nav(`/inventory/production/${r.run_id}`)} />}
    </div>
  )
}

function StartRun({ onClose, onStarted }) {
  const locations = useLocations()
  const [products, setProducts] = useState(null)
  const [productId, setProductId] = useState('')
  const [count, setCount] = useState('')
  const [locationId, setLocationId] = useState('')
  const [day, setDay] = useState(localToday())
  const [notes, setNotes] = useState('')
  const [needs, setNeeds] = useState([])
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')

  // Anything kept in stock except materials; a recipe is optional.
  useEffect(() => { listProductionProducts().then((p) => setProducts((p || []).filter((x) => x.stock_kind !== 'material'))).catch(() => setProducts([])) }, [])
  const chosen = (products || []).find((p) => String(p.product_id) === productId)
  useEffect(() => { if (locations?.length && !locationId) setLocationId(String(locations[0].location_id)) }, [locations, locationId])
  useEffect(() => {
    if (!productId || !(Number(count) > 0)) { setNeeds([]); return }
    const t = setTimeout(() => productionNeeds(productId, count).then(setNeeds).catch(() => setNeeds([])), 250)
    return () => clearTimeout(t)
  }, [productId, count])
  const short = needs.filter((n) => n.short > 0)
  const estimate = needs.reduce((t, n) => t + n.est_cost, 0)

  const start = async () => {
    setSaving(true)
    setErrors({})
    setError('')
    try {
      onStarted(await startRun({ product_id: Number(productId), qty: count, location_id: locationId ? Number(locationId) : undefined, date: day, notes }))
    } catch (e) {
      setErrors(e.fields || {})
      setError(e.fields ? '' : (e.message || 'Could not start the run.'))
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Start a run"
      subtitle="A batch of one product. Add its materials, labour and overhead while it's in progress, then finish it."
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={start} loading={saving} disabled={!productId || !(Number(count) > 0) || short.length > 0}><Factory size={14} />Start the run</Button>
      </>}
    >
      {error && <ErrorBanner message={error} />}
      {products && !products.length ? (
        <p className="sum-muted">No products yet. Add the product you make under Master Files → Products and services, then start a run here.</p>
      ) : (
        <>
          <div className="ci-form-meta-top two">
            <Field label="Making" error={errors.product_id}>
              <select value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">Choose a product…</option>
                <CreateOption kind="product" />
                {(products || []).map((p) => <option key={p.product_id} value={p.product_id}>{p.name}{p.sku ? ` · ${p.sku}` : ''}</option>)}
              </select>
            </Field>
            <Field label={<>How many <HelpTip text="How many you plan to make in this batch. You enter how many came out good when you finish it." /></>} error={errors.qty}>
              <input inputMode="decimal" value={count} onChange={(e) => setCount(e.target.value)} placeholder="0" />
            </Field>
          </div>
          <div className="ci-form-meta-top two">
            {locations?.length > 1 && (
              <Field label="Where">
                <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                  {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
                  <CreateOption kind="location" />
                </select>
              </Field>
            )}
            <Field label="Started on" error={errors.date}>
              <input type="date" value={day} max={localToday()} onChange={(e) => setDay(e.target.value)} />
            </Field>
          </div>
          {needs.length > 0 && (
            <table className="ci-table prod-needs">
              <thead><tr><th>Material</th><th className="num">Needed</th><th className="num">In stock</th><th className="num">About</th></tr></thead>
              <tbody>
                {needs.map((n) => (
                  <tr key={n.component_id}>
                    <td>{n.name}</td>
                    <td className="num">{qty(n.needed)}</td>
                    <td className={`num${n.short > 0 ? ' sum-warn' : ''}`}>{qty(n.qty_on_hand)}{n.short > 0 && <span className="cell-sub">{qty(n.short)} short</span>}</td>
                    <td className="num">{money(n.est_cost)}</td>
                  </tr>
                ))}
                <tr className="ci-total"><td colSpan="3">Materials, about</td><td className="num">{money(estimate)}</td></tr>
              </tbody>
            </table>
          )}
          {chosen && productId && Number(count) > 0 && !needs.length && (
            <p className="doc-hint">{chosen.name} has no recipe, so no materials are taken out now. Add them to the run once it’s started: from stock, or what you bought for this batch.</p>
          )}
          {chosen && chosen.stock_kind !== 'made' && <p className="doc-hint">Starting a run marks {chosen.name} as made here{chosen.qty_on_hand > 0 ? '; its stock moves to Finished goods' : ''}.</p>}
          {short.length > 0 && <p className="sum-warn">Not enough {short.map((n) => n.name).join(', ')} in stock for this many.</p>}
          <Field label="Notes (optional)">
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Eid collection, size M" />
          </Field>
        </>
      )}
    </Modal>
  )
}
